import React, {FC, useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {usePrevious} from '../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {AsyncHandlerOf, Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {parseDomainName} from '../../../../libs/shared/helpers/src/utilities/stringHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {InlineLinkButton} from '../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {extractNonGenericAccountDomainsFromContactUids} from '../../../helpers/accountHelpers';
import {
  CardAttributeUpdateAction,
  deleteAccount,
  updateAccount,
} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {
  ContactDetailsConflictingFieldKeysEnum,
  ContactDetailsSectionWithConflictsEnum,
} from '../../../helpers/models/cardMergeHelpers';
import {isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {CardModel} from '../../../models/api/cardModel';
import {ResourceMap} from '../../../store/appTypes';
import {updateCardAttributeAsyncAction} from '../../../store/data/cards/cardsActions';
import {contactDetailsCreateAccountFormAction} from '../../../store/panels/contacts/contactDetailsPanelActions';
import {ContactDetailsCreateAccountSourceActionsEnum} from '../../../store/panels/contacts/contactDetailsPanelSubState';
import {EditInCrmButtonContainer} from '../../common/crm/buttons/editInCrmButtonContainer';
import {SearchAccountsDropdown} from '../../common/dropdowns/search/searchAccountsDropdown';
import {InlineEditingAccountPopover} from '../../common/inlineEditing/inlineEditingAccountPopover';
import {ContactDetailsVersionsEnum, useContactDetailsContext} from '../contactDetailsContext';
import {
  buildContactDetailsEditAccountEvent,
  ContactDetailsEditActionsEnum,
  ContactDetailsTrackingDisplayContextOptionsEnum,
} from '../contactDetailsTrackingHelpers';
import {ContactInformationAttribute} from '../contactInformation/contactInformationAttribute';
import {ContactMergeConflictingValue} from '../contactMerge/contactMergeConflictingValue';

/*
 * Props.
 */

interface ContactHeaderAccountProps {
  account: AccountModel | undefined;
  card: CardModel | undefined;
  accounts: ResourceMap<AccountModel>;
  crmSyncEditLink?: string;
  openAccountDetailsPopUp: Handler;
  onAddAccountAsync: AsyncHandlerOf<number>;
}

/*
 * Style.
 */

const StyledAccountActionContainerDiv = styled.div`
  display: flex;
  width: 100%;
`;

const StyledAccountButton = styled(InlineLinkButton)`
  line-height: 26px;
  overflow: hidden;
`;

const StyledAccountReadonlyContainerDiv = styled.div`
  line-height: 26px;
  overflow: hidden;
`;

const StyledNewAccountButton = styled(Button)`
  margin-left: -12px;
  margin-top: -4px;
`;

const StyledAccountButtonContentDiv = styled.div`
  ${ellipsis()};
`;

const StyledAddAccountButtonContentDiv = styled(StyledAccountButtonContentDiv)`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  ${ellipsis()};
`;

const StyledInlineEditingAccountPopover = styled(InlineEditingAccountPopover)`
  margin-top: -5px;
  margin-bottom: -4px;
`;

const StyledEditingAccountDiv = styled.div`
  ${ellipsis()};
`;

const StyledContactMergeConflictingValue = styled(ContactMergeConflictingValue)`
  line-height: 26px;
`;

const StyledMergeAccountContainerDiv = styled.div`
  margin: 0 8px 0 -8px;
`;

/*
 * Component.
 */

export const ContactHeaderAccount: FC<ContactHeaderAccountProps> = (props) => {
  const {account, card, accounts, crmSyncEditLink, openAccountDetailsPopUp, onAddAccountAsync} = props;
  const dispatch = useAppDispatch();
  const {panelId, version, mergeContext, isReadOnly, onTrackEvent} = useContactDetailsContext();
  const previousAccount = usePrevious(account);
  const [hasAccountActionOccurred, setHasAccountActionOccurred] = useState(false);

  // Check if we are merging and have a merge conflict
  const conflictingHeaderValues =
    mergeContext?.conflictingMergeValues?.[ContactDetailsSectionWithConflictsEnum.HEADER];
  const shouldShowAccountMergeConflict = Boolean(
    conflictingHeaderValues &&
      conflictingHeaderValues.some((value) => value.key === ContactDetailsConflictingFieldKeysEnum.ACCOUNT),
  );

  useEffect(() => {
    if (!card?.id || !hasAccountActionOccurred) {
      return;
    }

    // If we no longer have an account, we deleted it.
    if (!account && previousAccount) {
      if (onTrackEvent) {
        onTrackEvent(
          buildContactDetailsEditAccountEvent(
            card.id,
            previousAccount,
            ContactDetailsTrackingDisplayContextOptionsEnum.CONTACT,
            ContactDetailsEditActionsEnum.DELETE,
            card.namespace,
          ),
        );
      }
    }

    // If the accounts are different, we edited the account.
    if (account && account !== previousAccount) {
      if (onTrackEvent) {
        onTrackEvent(
          buildContactDetailsEditAccountEvent(
            card.id,
            account,
            ContactDetailsTrackingDisplayContextOptionsEnum.CONTACT,
            !previousAccount ? ContactDetailsEditActionsEnum.ADD : ContactDetailsEditActionsEnum.EDIT,
            card.namespace,
          ),
        );
      }
    }
  }, [previousAccount, account, card?.id, card?.namespace, hasAccountActionOccurred, onTrackEvent]);

  const updateCardAttribute = useCallback(
    (action: CardAttributeUpdateAction) => {
      if (!card) {
        return;
      }
      dispatch(
        updateCardAttributeAsyncAction({
          card,
          action,
          shouldShowUndoBanner: true,
          panelId,
        }),
      );
    },
    [card, dispatch, panelId],
  );

  const onNewAccountClick = useCallback(
    (search: string) => {
      const domain = parseDomainName(search) ?? getCardNewAccountDomain(card);
      // Strip off the TLD for the name.
      const preDefinedAccountName = (domain ?? '').split('.')[0];

      if (!card) {
        return;
      }
      dispatch(
        contactDetailsCreateAccountFormAction(
          panelId,
          ContactDetailsCreateAccountSourceActionsEnum.ACCOUNT_DETAILS,
          card.id,
          preDefinedAccountName,
          domain,
        ),
      );
    },
    [card, dispatch, panelId],
  );

  const onConfirmAccountSelect = useCallback(
    (newAccountId: number) => {
      setHasAccountActionOccurred(true);
      onAddAccountAsync(newAccountId);
    },
    [onAddAccountAsync],
  );

  const onSave = useCallback(
    async (newAccountId: number) => {
      if (!account?.id) {
        return;
      }
      setHasAccountActionOccurred(true);
      updateCardAttribute(updateAccount(account?.id, newAccountId));
    },
    [account?.id, updateCardAttribute],
  );

  // If this is a private card or on version 1, do not display anything.
  if (version === ContactDetailsVersionsEnum.VERSION_1 || (card && isTeammateNamespace(card.namespace))) {
    return null;
  }

  if (shouldShowAccountMergeConflict) {
    return maybeRenderMergeConflict(shouldShowAccountMergeConflict, accounts);
  }

  if (account) {
    return (
      <StyledAccountActionContainerDiv>
        <ContactInformationAttribute<number>
          style={{
            margin: '-4px 0 0 0',
            width: '100%',
          }}
          controlsStyle={{
            top: '0',
          }}
          interactionId="contact-details-account"
          customEditButton={maybeRenderEditInCrmButton(crmSyncEditLink, card)}
          renderValue={({isEditing, editedValue, onEditSave, onEditCancel}) => {
            if (isEditing) {
              return (
                <StyledInlineEditingAccountPopover
                  renderButtonContent={() => (
                    <StyledEditingAccountDiv>{account.name}</StyledEditingAccountDiv>
                  )}
                  accountId={editedValue}
                  onEditSave={onEditSave}
                  onEditCancel={onEditCancel}
                  onNewAccountClick={onNewAccountClick}
                />
              );
            }

            if (isReadOnly) {
              return (
                <StyledAccountReadonlyContainerDiv>
                  <StyledAccountButtonContentDiv>{account.name}</StyledAccountButtonContentDiv>
                </StyledAccountReadonlyContainerDiv>
              );
            }

            return (
              <StyledAccountButton onClick={openAccountDetailsPopUp}>
                <StyledAccountButtonContentDiv>{account.name}</StyledAccountButtonContentDiv>
              </StyledAccountButton>
            );
          }}
          currentAttributeValue={account.id}
          onSave={onSave}
          onDelete={() => {
            setHasAccountActionOccurred(true);
            updateCardAttribute(deleteAccount(account.id));
          }}
          shouldHideCopyOption
          shouldStartInEditMode={false}
          customEditLabel={<Trans id="contact-details-account-header-edit-account-label">Edit account</Trans>}
        />
      </StyledAccountActionContainerDiv>
    );
  }

  // Do not show the add to account when merging or if we are in readonly mode or if account needs to be edited in crm
  if (mergeContext.isMerging || isReadOnly || crmSyncEditLink) {
    return null;
  }

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => (
        <StyledAccountActionContainerDiv>
          <StyledNewAccountButton
            variant={ButtonVariantsEnum.LINK}
            size={VisualSizesEnum.SMALL}
            hasHover={isOpen}
          >
            <StyledAddAccountButtonContentDiv>
              <Trans id="contact-details-header-add-to-account">+ Add to account</Trans>
            </StyledAddAccountButtonContentDiv>
          </StyledNewAccountButton>
        </StyledAccountActionContainerDiv>
      )}
      renderModal={(popoverProps) => (
        <SearchAccountsDropdown
          popoverProps={popoverProps}
          placement="bottom"
          onConfirm={onConfirmAccountSelect}
          onNewAccountClick={onNewAccountClick}
        />
      )}
    />
  );
};

/*
 * Helpers.
 */

function maybeRenderEditInCrmButton(crmSyncEditLink?: string, card?: CardModel) {
  if (!crmSyncEditLink || !card || !card.externalInfo) {
    return null;
  }
  return <EditInCrmButtonContainer source={card.externalInfo.source} linkUrl={crmSyncEditLink} />;
}

function maybeRenderMergeConflict(
  shouldShowAccountMergeConflict: boolean,
  accounts: ResourceMap<AccountModel>,
) {
  if (!shouldShowAccountMergeConflict) {
    return null;
  }
  return (
    <StyledMergeAccountContainerDiv>
      <StyledContactMergeConflictingValue
        section={ContactDetailsSectionWithConflictsEnum.HEADER}
        sectionKey={ContactDetailsConflictingFieldKeysEnum.ACCOUNT}
        renderOption={(accountId) => {
          const account = accounts[Number(accountId)];
          if (account) {
            return account.name;
          }
          return null;
        }}
        shouldRemoveTopAndBottomPadding
      />
    </StyledMergeAccountContainerDiv>
  );
}

function getCardNewAccountDomain(card?: CardModel) {
  if (!card) {
    return undefined;
  }
  const domains = extractNonGenericAccountDomainsFromContactUids(card.contactUids);
  if (domains.length === 0) {
    return undefined;
  }
  return domains[0];
}
